import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/** Roughly one screenful on a laptop — far enough that "back to top" is a real saving. */
const SHOW_AFTER_PX = 320;

/**
 * A floating "Go to top" control, mounted once for the whole app.
 *
 * <p>It watches the WINDOW, not a container. Both authenticated layouts put `overflow-auto` on their
 * `<main>`, but no ancestor constrains height — `SidebarProvider` is `min-h-svh` and the student
 * shell is `min-h-screen` — so `<main>` grows to fit its content and never scrolls internally. The
 * document is what actually scrolls, on every route. If that ever changes, this is the component to
 * revisit.</p>
 *
 * <p>Stays mounted and hides with `invisible` rather than unmounting: `visibility: hidden` takes it
 * out of the tab order too, so a keyboard user never lands on a control they cannot see, and the
 * fade still has something to animate.</p>
 */
export function BackToTop() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Scroll fires far more often than the class needs to change, so reads are coalesced into one
    // per frame; without this every pixel of a trackpad flick would queue a React state update.
    let frame = 0;

    const read = () => {
      frame = 0;
      setIsVisible(window.scrollY > SHOW_AFTER_PX);
    };

    const schedule = () => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(read);
    };

    // The page may already be scrolled on mount — a reload part-way down, or a hash link.
    read();

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  const scrollToTop = () => {
    // Optional-called because jsdom does not implement matchMedia; in a browser it is always there.
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Go to top"
      // Logical inset so the button follows the writing direction rather than pinning to the right.
      className={cn(
        "fixed bottom-6 end-6 z-40 inline-flex h-11 min-w-11 items-center justify-center gap-2",
        "rounded-full bg-primary px-3.5 text-sm font-semibold text-primary-foreground sm:px-4",
        "shadow-[var(--shadow-brand)] transition-all duration-200 hover:bg-primary/90",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none",
        "print:hidden",
        isVisible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none invisible translate-y-2 opacity-0",
      )}
    >
      <ArrowUp className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="hidden sm:inline">Go to top</span>
    </button>
  );
}
